import { cn } from '@/lib/utils';
import Ionicons from '@expo/vector-icons/Ionicons';
import { type ComponentProps, type ReactNode } from 'react';
import { Pressable, Text, View } from 'react-native';

type IoniconsName = ComponentProps<typeof Ionicons>['name'];

export type NavItemVariant = 'primary' | 'secondary' | 'special' | 'admin';

interface SidebarNavItemProps {
  icon?: IoniconsName;
  customIcon?: ReactNode;
  activeIcon?: IoniconsName;
  label: string;
  isActive?: boolean;
  badge?: number;
  variant?: NavItemVariant;
  onPress: () => void;
}

export function SidebarNavItem({
  icon,
  customIcon,
  activeIcon,
  label,
  isActive = false,
  badge,
  variant = 'primary',
  onPress,
}: SidebarNavItemProps) {
  const iconName = isActive && activeIcon ? activeIcon : icon;

  return (
    <Pressable
      onPress={onPress}
      className={cn(
        'flex-row items-center rounded-lg mx-2 my-0.5 active:bg-surface',
        variant === 'special' && 'bg-primary',
        variant !== 'special' && isActive && 'bg-surface',
      )}
      style={{
        paddingVertical: 12,
        paddingHorizontal: 16,
      }}
    >
      <View style={{ width: 28, alignItems: 'center' }}>
        {customIcon || (
          <Ionicons
            name={iconName!}
            size={24}
            className={cn(
              variant === 'special' && 'text-background',
              variant !== 'special' && isActive && 'text-white',
              variant !== 'special' && !isActive && 'text-muted-foreground',
              variant === 'admin' && !isActive && 'text-warning',
              variant === 'admin' && isActive && 'text-warning',
            )}
          />
        )}
      </View>
      <Text
        className={cn(
          'text-base',
          isActive && 'font-semibold',
          variant === 'special' && 'text-background font-semibold',
          variant !== 'special' && isActive && 'text-white',
          variant !== 'special' && !isActive && 'text-foreground',
          variant === 'admin' && !isActive && 'text-warning',
          variant === 'admin' && isActive && 'text-warning',
        )}
        style={{ marginLeft: 12 }}
      >
        {label}
      </Text>
      {badge !== undefined && badge > 0 && (
        <View
          className="ml-auto bg-primary/20"
          style={{
            borderRadius: 4,
            minWidth: 20,
            height: 20,
            alignItems: 'center',
            justifyContent: 'center',
            paddingHorizontal: 6,
          }}
        >
          <Text className="text-xs font-semibold text-primary">
            {badge > 99 ? '99+' : badge}
          </Text>
        </View>
      )}
    </Pressable>
  );
}
